/**
 * This file imports various Swiper modules and components.
 *
 * The `// eslint-disable-next-line import/no-unresolved` comments are used to suppress ESLint errors
 * related to unresolved imports. This is necessary because Swiper does not support CommonJS
 * module resolution, as noted in https://github.com/import-js/eslint-plugin-import/issues/2266.
 */

import React, { useCallback, useEffect } from 'react'
import { useSetState, usePrevious } from 'react-use'
import PropTypes from 'prop-types'
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react'
// eslint-disable-next-line import/no-unresolved
import { Navigation, Keyboard, Pagination } from 'swiper/modules'
import Modal from 'react-modal'

import Icon from 'components/Icon'

// eslint-disable-next-line import/no-unresolved
import 'swiper/css'
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/navigation'
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/pagination'
import './overlay-gallery.pcss'

export const OverlayGalleryControllerContext = React.createContext()

const initState = Object.freeze({
    isOpen: false,
    images: null,
    initIndex: 0,
})

const OverlayGallery = ({ children, locationKey }) => {
    const [state, setState] = useSetState(initState)
    const prevLocationKey = usePrevious(locationKey)

    const { isOpen, images, initIndex } = state

    const openGallery = useCallback((images, initIndex = 0) => {
        setState({
            isOpen: true,
            images: images,
            initIndex: initIndex,
        })
    }, [setState])

    const closeGallery = useCallback(() => {
        setState(initState)
    }, [setState])

    // Check if gallery should be closed due to route change
    useEffect(() => {
        if (isOpen && locationKey !== prevLocationKey) {
            closeGallery()
        }
    }, [locationKey, prevLocationKey, isOpen, closeGallery])

    return (
        <OverlayGalleryControllerContext.Provider
            value={{
                isOpen: isOpen,
                openGallery: openGallery,
                closeGallery: closeGallery,
            }}
        >
            {isOpen && (
                <Modal
                    isOpen={isOpen}
                    onRequestClose={closeGallery}
                    className="modal-content"
                    overlayClassName="modal-overlay"
                    contentLabel="Image Gallery"
                    ariaHideApp={false}
                >
                    <button className="close-button" onClick={closeGallery}>
                        <Icon fa name="times" color="#FFF" />
                    </button>
                    <div className="swiper-container">
                        <div className="swiper-container">
                            <Swiper
                                modules={[Navigation, Keyboard, Pagination]}
                                navigation
                                pagination={{ clickable: true }}
                                keyboard={{ enabled: true }}
                                initialSlide={initIndex}
                                loop={true}
                                className="mySwiper"
                            >
                                {images.map((image, index) => (
                                    <SwiperSlide key={index}>
                                        <img
                                            src={image.src}
                                            alt={image.alt || `Image ${index + 1}`}
                                            className="swiper-image"
                                        />
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    </div>
                </Modal>
            )}
            {children}
        </OverlayGalleryControllerContext.Provider>
    )
}

OverlayGallery.propTypes = {
    children: PropTypes.any.isRequired,
    locationKey: PropTypes.string.isRequired,
}

export default OverlayGallery
